/**
 * NumberField — a "type-freely, clamp-on-blur" numeric input.
 *
 * Fixes the controlled-number-input trap where clearing the field coerces it to
 * the min (so you can't delete-and-retype). Keeps a local draft while editing so
 * "" / partial values are allowed; commits valid in-range numbers on change (for
 * live feedback) and clamps to [min, max] on blur. Modeled on the exclusion-rule
 * ThresholdInput pattern.
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  type FC,
  type InputHTMLAttributes,
  useEffect,
  useState,
} from 'react';

const NUMERIC = /^-?\d*\.?\d*$/;

export interface NumberFieldProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'value' | 'onChange' | 'min' | 'max' | 'type'
  > {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  /** Round to an integer on commit (default true). */
  integer?: boolean;
}

function clampValue(n: number, min?: number, max?: number, integer = true): number {
  let c = n;
  if (min != null) c = Math.max(min, c);
  if (max != null) c = Math.min(max, c);
  return integer ? Math.round(c) : c;
}

export const NumberField: FC<NumberFieldProps> = ({
  value,
  onChange,
  min,
  max,
  integer = true,
  onFocus,
  onBlur,
  onKeyDown,
  ...rest
}) => {
  const [draft, setDraft] = useState(() => String(value));
  const [focused, setFocused] = useState(false);

  // Mirror the external value when the user isn't actively editing.
  useEffect(() => {
    if (!focused) setDraft(String(value));
  }, [value, focused]);

  const handleChange = (raw: string) => {
    if (raw !== '' && !NUMERIC.test(raw)) return; // ignore stray characters
    setDraft(raw);
    if (raw === '' || raw === '-' || raw === '.' || raw === '-.') return; // partial — don't commit
    const parsed = integer ? parseInt(raw, 10) : parseFloat(raw);
    if (!Number.isFinite(parsed)) return;
    const c = clampValue(parsed, min, max, integer);
    if (c !== value) onChange(c);
  };

  const finalize = (raw: string) => {
    const parsed = integer ? parseInt(raw, 10) : parseFloat(raw);
    const c = clampValue(Number.isFinite(parsed) ? parsed : value, min, max, integer);
    setDraft(String(c));
    if (c !== value) onChange(c);
  };

  return (
    <input
      {...rest}
      type="number"
      min={min}
      max={max}
      value={draft}
      onFocus={(e) => {
        setFocused(true);
        e.currentTarget.select();
        onFocus?.(e);
      }}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={(e) => {
        setFocused(false);
        finalize(e.target.value);
        onBlur?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        onKeyDown?.(e);
      }}
    />
  );
};

export default NumberField;
